import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { TimeOffPolicyAccrualSummary } from "@/pages/settings/time-off/policy/components/TimeOffPolicyAccrualSummary.tsx";
import {
  accrualWithDateTypes,
  accrualWithTwoDateTypes,
  createDayOfMonthChangeHandler,
  getDayOfMonthOptions,
  getStartAmountOptions,
} from "@/pages/settings/time-off/policy/time-off-policy.types.ts";
import { TimeOffCategoryPolicySchema } from "@/pages/settings/time-off/policy/TimeOffCategoryPolicyEdit.tsx";
import {
  ApiTimeOffAccrualFrequencyTypes,
  ApiTimeOffDateType,
  ApiTimeOffPeriodTrackingTime,
  ApiTimeOffPolicyAccrualAtType,
  ApiTimeOffPolicyMilestone,
  ApiTimeOffPolicyVersion,
  getAccrualAtTypes,
  getAccrualFrequencyTypes,
  getCarryoverExpiringDateTypes,
} from "@/types/time-off/time-off-policy.types.ts";
import { Box, Input, Select, Stack, Switch, Title } from "@skailer-com/uikit";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface TimeOffPolicyAccrueSettingsProps {
  formHook: UseFormReturn<TimeOffCategoryPolicySchema>;
  periodTrackType?: ApiTimeOffPeriodTrackingTime;
  currentVersion?: ApiTimeOffPolicyVersion;
}

export function TimeOffPolicyAccrueSettings({
  formHook,
  periodTrackType,
  currentVersion,
}: TimeOffPolicyAccrueSettingsProps) {
  const { t } = useTranslation();
  const {
    watch,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = formHook;
  const isHours = periodTrackType === ApiTimeOffPeriodTrackingTime.hours;

  const { append } = useFieldArray({ control, name: "milestones" });

  useEffect(() => {
    if (getValues("milestones").length === 0) {
      append({
        title: "",
        start_amount: 0,
        start_date_type: ApiTimeOffDateType.month,
        start_balance_amount: 0,
        accrual_frequency_type: ApiTimeOffAccrualFrequencyTypes.monthly,
        accrual_amount: 0,
        accrual_at_type: ApiTimeOffPolicyAccrualAtType.start_of_accrual_period,
        accrual_cap_enabled: false,
        accrual_cap_amount: 0,
      } as ApiTimeOffPolicyMilestone);
    }
  }, [append, getValues]);

  const milestone = watch("milestones")?.[0];
  const milestoneErrors = errors.milestones?.[0];

  const startDateType = milestone?.start_date_type ?? ApiTimeOffDateType.month;
  const accrualFrequencyType =
    milestone?.accrual_frequency_type ?? ApiTimeOffAccrualFrequencyTypes.monthly;
  const accrualAmount = milestone?.accrual_amount ?? 0;
  const accrualAtType =
    milestone?.accrual_at_type ?? ApiTimeOffPolicyAccrualAtType.start_of_accrual_period;

  const withDateInAccrual = accrualWithDateTypes.includes(accrualFrequencyType);
  const withTwoDateInAccrual = accrualWithTwoDateTypes.includes(accrualFrequencyType);

  useEffect(() => {
    if (!withDateInAccrual || !milestone) return;
    const jan1 = new Date(new Date().getFullYear(), 0, 1);
    if (!getValues("milestones.0.accrual_frequency_data.date_first")) {
      setValue("milestones.0.accrual_frequency_data.date_first", jan1);
    }
    if (withTwoDateInAccrual && !getValues("milestones.0.accrual_frequency_data.date_second")) {
      setValue("milestones.0.accrual_frequency_data.date_second", jan1);
    }
  }, [withDateInAccrual, withTwoDateInAccrual, milestone, getValues, setValue]);

  const startAmountOptions = useMemo(() => getStartAmountOptions(startDateType), [startDateType]);
  const dayOfMonthOptions = getDayOfMonthOptions();
  const accrualFrequencyTypeOptions = getAccrualFrequencyTypes();
  const accrualAtTypeOptions = getAccrualAtTypes();
  const carryoverExpiringDateTypeOptions = getCarryoverExpiringDateTypes();

  const firstMilestone = currentVersion?.milestones?.[0];
  const [startsFromHireDate, setStartsFromHireDate] = useState(
    firstMilestone ? Number(firstMilestone.start_amount) === 0 : true
  );
  const [startBalanceEnabled, setStartBalanceEnabled] = useState(
    firstMilestone ? Number(firstMilestone.start_balance_amount) > 0 : false
  );
  const [syncedVersionId, setSyncedVersionId] = useState(currentVersion?.id);

  if (currentVersion?.id !== syncedVersionId) {
    setSyncedVersionId(currentVersion?.id);
    if (firstMilestone) {
      setStartsFromHireDate(Number(firstMilestone.start_amount) === 0);
      setStartBalanceEnabled(Number(firstMilestone.start_balance_amount) > 0);
    }
  }

  if (!milestone) return null;

  return (
    <Box>
      <Title header={t("time_off:accrue_settings")} size={"xs"} paddingBottom />
      <Box variant={"border"} className={"px-6"}>
        <Title header={t("time_off:leave_earnings")} size={"xs"} paddingBottom />
        <Stack gap={"md"} className={"mb-4"}>
          <Stack gap={"md"} direction={"horizontal"} className={"grid md:grid-cols-3"}>
            <Box>
              <Input
                type={"text"}
                {...formHook.register("milestones.0.accrual_amount")}
                label={isHours ? t("time_off:hours") : t("time_off:days")}
                error={milestoneErrors?.accrual_amount?.message}
              />
            </Box>
            <Box>
              <Controller
                control={control}
                name={"milestones.0.accrual_frequency_type"}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("common:period")}
                    options={accrualFrequencyTypeOptions}
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    error={milestoneErrors?.accrual_frequency_type?.message}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                control={control}
                name={"milestones.0.accrual_at_type"}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("time_off:accrual_at_type_title", {
                      units: isHours
                        ? t("time_off:hours_genitive").toLowerCase()
                        : t("time_off:days_genitive").toLowerCase(),
                    })}
                    options={accrualAtTypeOptions}
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    error={milestoneErrors?.accrual_at_type?.message}
                  />
                )}
              />
            </Box>
          </Stack>

          {withDateInAccrual && (
            <Stack direction={"horizontal"} gap={"md"} className={"grid md:grid-cols-3"}>
              {(accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.monthly ||
                accrualFrequencyType ===
                  ApiTimeOffAccrualFrequencyTypes.monthly_annual_proportional) && (
                <Box>
                  <Controller
                    control={control}
                    name={"milestones.0.accrual_frequency_data.date_first"}
                    render={({ field: { value, onChange } }) => {
                      const selectedDay = (value as Date | undefined)?.getDate() || 1;
                      return (
                        <Select
                          label={t("common:period_start_date")}
                          options={dayOfMonthOptions}
                          emptyTitle={t("common:select")}
                          value={selectedDay}
                          onChange={createDayOfMonthChangeHandler(value as Date | null, onChange)}
                        />
                      );
                    }}
                  />
                </Box>
              )}
              {accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.semimonthly && (
                <>
                  <Box>
                    <Controller
                      control={control}
                      name={"milestones.0.accrual_frequency_data.date_first"}
                      render={({ field: { value, onChange } }) => {
                        const selectedDay = (value as Date | undefined)?.getDate() || 1;
                        return (
                          <Select
                            label={t("common:period_start_dates")}
                            options={dayOfMonthOptions}
                            emptyTitle={t("common:select")}
                            value={selectedDay}
                            onChange={createDayOfMonthChangeHandler(value as Date | null, onChange)}
                          />
                        );
                      }}
                    />
                  </Box>
                  <Box>
                    <Controller
                      control={control}
                      name={"milestones.0.accrual_frequency_data.date_second"}
                      render={({ field: { value, onChange } }) => {
                        const selectedDay = (value as Date | undefined)?.getDate() || 1;
                        return (
                          <Select
                            label={t("common:period_start_dates")}
                            options={dayOfMonthOptions}
                            emptyTitle={t("common:select")}
                            value={selectedDay}
                            onChange={createDayOfMonthChangeHandler(value as Date | null, onChange)}
                          />
                        );
                      }}
                    />
                  </Box>
                </>
              )}
              {(accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.semiannual ||
                accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.yearly) && (
                <>
                  <Box className={"w-48"}>
                    <Controller
                      control={control}
                      name={"milestones.0.accrual_frequency_data.date_first"}
                      render={({ field: { value, onChange } }) => (
                        <Datepicker
                          showYearDropdown={false}
                          value={value as Date | null | undefined}
                          onChange={onChange}
                          label={
                            accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.semiannual
                              ? t("common:first_date")
                              : t("common:date")
                          }
                        />
                      )}
                    />
                  </Box>
                  {withTwoDateInAccrual && (
                    <Box className={"w-48"}>
                      <Controller
                        control={control}
                        name={"milestones.0.accrual_frequency_data.date_second"}
                        render={({ field: { value, onChange } }) => (
                          <Datepicker
                            showYearDropdown={false}
                            value={value as Date | null | undefined}
                            onChange={onChange}
                            label={t("common:second_date")}
                          />
                        )}
                      />
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}
          <Stack>
            <TimeOffPolicyAccrualSummary
              isHours={isHours}
              accrualAmount={accrualAmount}
              accrualFrequencyType={accrualFrequencyType}
              accrualAtType={accrualAtType}
            />
          </Stack>
        </Stack>

        <Stack>
          <Title header={t("common:settings")} size={"xs"} paddingBottom={true} paddingTop={true} />
          <Stack gap={"md"}>
            <Box>
              <Switch
                checked={startsFromHireDate}
                onChange={(newValue) => {
                  setStartsFromHireDate(newValue);
                  if (newValue) {
                    setValue("milestones.0.start_amount", 0);
                  }
                }}
                size={"sm"}
                title={t("time_off:milestone_starts_from_hire_date")}
                description={t("time_off:milestone_starts_from_hire_date_caption")}
              />
              {!startsFromHireDate && (
                <Stack gap={"md"} direction={"horizontal"} className={"pl-9 grid md:grid-cols-4"}>
                  <Box>
                    <Controller
                      control={control}
                      name={"milestones.0.start_date_type"}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          label={t("common:period")}
                          options={carryoverExpiringDateTypeOptions}
                          emptyTitle={t("common:select")}
                          value={value}
                          onChange={onChange}
                          error={milestoneErrors?.start_date_type?.message}
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      control={control}
                      name={"milestones.0.start_amount"}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          label={t("time_off:milestone_start_amount_title")}
                          options={startAmountOptions}
                          emptyTitle={t("common:select")}
                          value={String(value)}
                          onChange={(v) => onChange(Number(v))}
                          searchable={true}
                          error={milestoneErrors?.start_amount?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              )}
            </Box>
            <Box>
              <Switch
                checked={startBalanceEnabled}
                onChange={(newValue) => {
                  setStartBalanceEnabled(newValue);
                  if (!newValue) {
                    setValue("milestones.0.start_balance_amount", 0);
                  }
                }}
                size={"sm"}
                title={t("time_off:milestone_start_balance_title")}
                description={t("time_off:milestone_start_balance_caption") + "."}
              />
              {startBalanceEnabled && (
                <Box className={"pl-9 w-88"}>
                  <Input
                    type={"text"}
                    {...formHook.register("milestones.0.start_balance_amount")}
                    label={t("time_off:milestone_start_balance_lable", {
                      units: isHours
                        ? t("time_off:hours").toLowerCase()
                        : t("time_off:days").toLowerCase(),
                    })}
                  />
                </Box>
              )}
            </Box>
            <Box>
              <Controller
                control={control}
                name={"milestones.0.accrual_cap_enabled"}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value as boolean}
                    onChange={onChange}
                    size={"sm"}
                    title={t("time_off:accrual_cap_title")}
                    description={t("time_off:accrual_cap_yes_caption") + "."}
                  />
                )}
              />
              {milestone.accrual_cap_enabled && (
                <Box className={"pl-9 w-88"}>
                  <Input
                    type={"text"}
                    {...formHook.register("milestones.0.accrual_cap_amount")}
                    label={t("time_off:accrual_cap_yes_label", {
                      units: isHours
                        ? t("time_off:hours").toLowerCase()
                        : t("time_off:days").toLowerCase(),
                    })}
                  />
                </Box>
              )}
            </Box>
            <Box>
              <Controller
                control={control}
                name={"contractual_fte_proration_enabled"}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value as boolean}
                    onChange={onChange}
                    size={"sm"}
                    title={t("time_off:fte_proration_title")}
                    description={t("time_off:fte_proration_caption")}
                  />
                )}
              />
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
